import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { CHART_COLORS }                from '../typings/chart';
import { ChartPointModel }             from '../typings/intelligence.chart-point';
import { ChartSeriesModel }            from '../typings/intelligence.chart-series';
import { IntelligenceFormatService }   from '../intelligence.format.service';
import { IntelligenceOperationsModel } from '../typings/intelligence.operations';
import { IntelligenceService }         from '../intelligence.service';

@Component({
  selector   : 'wor-intelligence-operations-tab',
  template: require('./intelligence.operations-tab.component.html')
})
export class OperationsTabComponent implements OnInit, OnDestroy {
  error = false;
  loading = true;

  conversionValue = '';
  openJobsValue = '';
  turnaroundValue = '';

  jobsPoints : Array<ChartPointModel> = [];
  messagesPoints : Array<ChartPointModel> = [];

  jobsSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 1 ], key: 'jobs_completed', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.JOBS_COMPLETED') },
    { color: CHART_COLORS[ 3 ], key: 'bookings_created', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.BOOKINGS_CREATED') },
    { color: CHART_COLORS[ 2 ], key: 'bookings_converted_to_invoice', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.CONVERTED') }
  ];

  messagesSeries : Array<ChartSeriesModel> = [
    { color: CHART_COLORS[ 0 ], key: 'sms_sent', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.SMS') },
    { color: CHART_COLORS[ 1 ], key: 'emails_sent', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.EMAIL') },
    { color: CHART_COLORS[ 2 ], key: 'documents_sent', label: this.translateService.instant('INTELLIGENCE_SPACE.OPERATIONS.DOCUMENTS') }
  ];

  private loadGeneration = 0;
  private subscription : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  isJobsEmpty () : boolean {
    return !this.jobsPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }

  isMessagesEmpty () : boolean {
    return !this.messagesPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getOperations()
    .then(( data : IntelligenceOperationsModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.jobsPoints = ( data.series || [] ).map(day => ({
        label : this.intelligenceFormatService.date(day.date),
        values: {
          bookings_converted_to_invoice: day.bookings_converted_to_invoice,
          bookings_created             : day.bookings_created,
          jobs_completed               : day.jobs_completed
        }
      }));

      this.messagesPoints = ( data.messages || [] ).map(day => ({
        label : this.intelligenceFormatService.date(day.date),
        values: {
          documents_sent: day.documents_sent,
          emails_sent   : day.emails_sent,
          sms_sent      : day.sms_sent
        }
      }));

      this.conversionValue = this.intelligenceFormatService.percent(data.booking_conversion_rate);
      this.openJobsValue = this.intelligenceFormatService.count(data.open_jobs_now);
      this.turnaroundValue = this.intelligenceFormatService.hours(this.meanTurnaround(data.series || []));
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  private meanTurnaround ( series : Array<{ avg_job_turnaround_hours : number }> ) : number {
    const values = series
      .map(day => day.avg_job_turnaround_hours)
      .filter(value => value !== null && value !== undefined);

    return values.length
      ? values.reduce(( sum, value ) => sum + value, 0) / values.length
      : 0;
  }
}
